import { useState } from "react";
import { formatMoney, type CurrencyFormat } from "../../domain/money/format";
import type { Money } from "../../domain/money/money";
import type { StatutoryDiscountResult } from "../../domain/pricing/statutory";
import type { StatutoryDiscount as StatutoryDiscountInput } from "./useCart";

/**
 * Senior Citizen / PWD discount capture (FR-028, FR-029, FR-030, T177).
 *
 * REQUIRES CARDHOLDER NAME AND ID BEFORE ACCEPTING (FR-029) — the Apply
 * button stays disabled until both are filled in, and both print on the
 * receipt (compose.ts). This is not optional paperwork; it is what the
 * discount's legal basis actually requires.
 *
 * COVERED/TOTAL CUSTOMERS DEFAULTS TO 1/1 — the common case (a solo
 * cardholder) needs no extra taps. Shared-bill proration (FR-030) is there
 * for the less common case without making the ordinary one slower.
 *
 * Same "a manager has to approve this" pattern as DiscountDialog — approval
 * is requested by the caller (Till.tsx), so one approval dialog continues
 * to serve every kind of discount, void, refund, and cash variance.
 */

export interface StatutoryDiscountProps {
  currency: CurrencyFormat;
  eligibleAmount: Money;
  /** Live preview as the form fields change, or null while unusable. */
  preview(input: {
    discountType: "senior_citizen" | "pwd";
    coveredCustomers: number;
    totalCustomers: number;
  }): StatutoryDiscountResult | null;
  currentDiscount: StatutoryDiscountInput | null;
  /** authorizedBy is filled in by the caller once a manager approves. */
  onApply(input: Omit<StatutoryDiscountInput, "authorizedBy">): void;
  onRemove(): void;
  onCancel(): void;
}

export function StatutoryDiscount({
  currency,
  eligibleAmount,
  preview,
  currentDiscount,
  onApply,
  onRemove,
  onCancel,
}: StatutoryDiscountProps) {
  const [discountType, setDiscountType] = useState<"senior_citizen" | "pwd">(
    currentDiscount?.discountType ?? "senior_citizen",
  );
  const [cardholderName, setCardholderName] = useState(currentDiscount?.cardholderName ?? "");
  const [cardholderId, setCardholderId] = useState(currentDiscount?.cardholderId ?? "");
  const [totalCustomers, setTotalCustomers] = useState(
    String(currentDiscount?.totalCustomers ?? 1),
  );
  const [coveredCustomers, setCoveredCustomers] = useState(
    String(currentDiscount?.coveredCustomers ?? 1),
  );

  const total = Number.parseInt(totalCustomers, 10);
  const covered = Number.parseInt(coveredCustomers, 10);
  const countsValid =
    Number.isInteger(total) && total >= 1 && Number.isInteger(covered) && covered >= 1 && covered <= total;

  const result = countsValid
    ? preview({ discountType, coveredCustomers: covered, totalCustomers: total })
    : null;

  const ready = countsValid && cardholderName.trim() !== "" && cardholderId.trim() !== "";

  return (
    <div className="statutory" role="dialog" aria-label="Senior Citizen / PWD discount">
      <h2 className="statutory__title">Senior Citizen / PWD discount</h2>

      <fieldset className="statutory__type">
        <label className="statutory__type-option">
          <input
            type="radio"
            name="statutory-type"
            checked={discountType === "senior_citizen"}
            onChange={() => setDiscountType("senior_citizen")}
          />
          <span>Senior Citizen</span>
        </label>
        <label className="statutory__type-option">
          <input
            type="radio"
            name="statutory-type"
            checked={discountType === "pwd"}
            onChange={() => setDiscountType("pwd")}
          />
          <span>PWD</span>
        </label>
      </fieldset>

      <label className="statutory__field">
        <span className="statutory__label">Cardholder name</span>
        <input
          className="statutory__input"
          value={cardholderName}
          onChange={(event) => setCardholderName(event.target.value)}
          autoComplete="off"
        />
      </label>

      <label className="statutory__field">
        <span className="statutory__label">ID number</span>
        <input
          className="statutory__input"
          value={cardholderId}
          onChange={(event) => setCardholderId(event.target.value)}
          autoComplete="off"
        />
      </label>

      <div className="statutory__counts">
        <label className="statutory__field">
          <span className="statutory__label">Total at the table</span>
          <input
            className="statutory__input statutory__input--number"
            inputMode="numeric"
            value={totalCustomers}
            onChange={(event) => {
              setTotalCustomers(event.target.value);
              // Covered can never exceed total — nudge it down rather than
              // leaving an invalid combination on screen.
              const nextTotal = Number.parseInt(event.target.value, 10);
              if (Number.isInteger(nextTotal) && covered > nextTotal) {
                setCoveredCustomers(String(nextTotal));
              }
            }}
          />
        </label>
        <label className="statutory__field">
          <span className="statutory__label">Covered by this discount</span>
          <input
            className="statutory__input statutory__input--number"
            inputMode="numeric"
            value={coveredCustomers}
            onChange={(event) => setCoveredCustomers(event.target.value)}
          />
        </label>
      </div>

      {!countsValid && (
        <p className="statutory__error" role="alert">
          Covered customers can be 1 up to the total at the table.
        </p>
      )}

      {result && (
        <p className="statutory__preview">
          Eligible amount <span className="money">{formatMoney(eligibleAmount, currency)}</span>
          {" — "}
          takes off <span className="money">{formatMoney(result.discountAmount, currency)}</span>,
          new total for the covered share{" "}
          <span className="money">{formatMoney(result.amountDue, currency)}</span>
        </p>
      )}

      <p className="statutory__note">
        Cardholder name and ID print on the receipt (FR-029). A manager has to approve this.
      </p>

      <div className="statutory__actions">
        <button type="button" onClick={onCancel}>
          Cancel
        </button>

        {currentDiscount && (
          <button type="button" className="statutory__remove" onClick={onRemove}>
            Remove discount
          </button>
        )}

        <button
          type="button"
          className="is-primary"
          disabled={!ready}
          onClick={() =>
            onApply({
              discountType,
              cardholderName: cardholderName.trim(),
              cardholderId: cardholderId.trim(),
              coveredCustomers: covered,
              totalCustomers: total,
            })
          }
        >
          Apply
        </button>
      </div>
    </div>
  );
}

export default StatutoryDiscount;
